import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Container, Row, Col, Form, Button, Card, Alert, Spinner, Image } from "react-bootstrap";
import "../../assets/css/InvoiceForm.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { adminInvoicesAPI, usersAPI } from "../../api/apiClient";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

export default function InvoiceForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const fileRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [data, setData] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  
  // تعريف الحقول المطلوبة
  const requiredFields = useMemo(() => [
    'title', 
    'rechnungsnummer', 
    'ausstellungsdatum', 
    'zahlungsziel'
  ], []);

  const [formData, setFormData] = useState({
    logo: "",
    title: "",
    firma_name: "",
    kunde_name: "",
    strasse_hausnummer: "",
    postleitzahl_staat_ort: "",
    land: "Deutschland",
    email: "",
    ust_idnr: "",
    rechnung: "RECHNUNG",
    unsrer_firma: "TransCiroX GmBH",
    postleitzahl_staat_ort_2: "51766 Engelskirchen",
    strasse_hausnummer_2: "Hauptstraße 10",
    land_2: "Deutschland",
    st_nr: "212/5711/3152",
    ust_idnr_2: "DE456589185",
    zaher_rawas: "TransCirox",
    telefonnummer: "+4917655439634",
    email_2: "Kontakt@transcirox.com",
    webseite: "www.transcirox.com",
    rechnungsnummer: "",
    kundennummer: "",
    zahlungsziel: "",
    ausstellungsdatum: new Date().toISOString().split('T')[0],
    zahlungsform: "Überweisung",
    bedanken_uns: "Wir bedanken uns für Ihren Auftrag und stellen folgende Positionen in Rechnung:",
    menge: "",
    menge_weight: "",
    preis: "",
    rabatt_prozent: "",
    betrag: "",
    auftrag_nr: "",
    abholung: "",
    zustellung: "",
    betrag_2: "",
    nettobetrag: "",
    ust_19_von: "",
    gesamtbetrag: "",
    zu_bezahlen: "",
    bankverbindung: "BANKVERBINDUNG:",
    zahlungsempfanger: " TransCirox GmbH",
    bankname: "Volksbank Oberberg eg",
    iban: "DE29 3846 2135 7117 7120 14",
    swift_bic: "GENODED1WIL",
  });

  // دالة التحقق من صحة الحقول
  const validateField = useCallback((name, value) => {
    if (requiredFields.includes(name) && (!value || value.trim() === '')) {
      return t('field_required');
    }
    
    // تحقق مخصص للبريد الإلكتروني
    if (name === 'email' && value && !/\S+@\S+\.\S+/.test(value)) {
      return t('invalid_email');
    }
    
    return null;
  }, [requiredFields, t]);

  // دالة التحقق من جميع الحقول
  const validateForm = useCallback(() => {
    const errors = {};
    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
      }
    });
    return errors;
  }, [formData, requiredFields, validateField]);

  // تحديث التحقق عند تغيير الحقول
  useEffect(() => {
    const errors = validateForm();
    setValidationErrors(errors);
  }, [formData, validateForm]);

  // معالجة فقدان التركيز من حقل
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouchedFields(prev => ({ ...prev, [name]: true }));
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const response = await usersAPI.index();

      if (response.success) {
        const fetchedData = response.data.data || response.data;
        setData(fetchedData);
      } else {
        setError(response.message || t('failed_to_fetch_users'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_users'));
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (e) => {
    const userId = e.target.value;
    
    if (!userId) {
      // Reset user data if no user selected
      setSelectedUser(null);
      setFormData(prev => ({
        ...prev,
        firma_name: "",
        kunde_name: "",
        strasse_hausnummer: "",
        postleitzahl_staat_ort: "",
        land: "Deutschland",
        email: "",
        ust_idnr: ""
      }));
      return;
    }

    const user = data.find(user => user.id.toString() === userId);
    
    if (user) {
      setSelectedUser(user);
      
      // Auto-fill form fields with user data
      setFormData(prev => ({
        ...prev,
        firma_name: user.firstname ?? "",
        kunde_name: user.lastname ?? "",
        strasse_hausnummer: user.street_address || "",
        postleitzahl_staat_ort: user.postal_code || "",
        land: user.addressone || "Deutschland",
        email: user.email || "",
        ust_idnr: user.tax_number || ""
      }));
    }
  };

  // Memoized calculation function
  const calculateInvoiceTotals = useCallback((preis, rabatt_prozent) => {
    const price = parseFloat(preis) || 0;
    const discount = parseFloat(rabatt_prozent) || 0;
    const netAmount = price * (1 - discount / 100);
    const vatAmount = netAmount * 0.19;
    const totalAmount = netAmount + vatAmount;

    return {
      betrag: isNaN(netAmount) ? "" : netAmount.toFixed(2),
      betrag_2: isNaN(vatAmount) ? "" : vatAmount.toFixed(2),
      nettobetrag: isNaN(netAmount) ? "" : netAmount.toFixed(2),
      ust_19_von: isNaN(netAmount) ? "" : netAmount.toFixed(2),
      gesamtbetrag: isNaN(totalAmount) ? "" : totalAmount.toFixed(2),
      zu_bezahlen: isNaN(totalAmount) ? "" : totalAmount.toFixed(2),
    };
  }, []);

  // Fetch invoice data for edit mode
  useEffect(() => {
    if (id) {
      const fetchInvoice = async () => {
        try {
          setLoading(true);
          setError("");
          const response = await adminInvoicesAPI.show(id);
          
          if (response.success) {
            const invoiceData = response.data.data;
            // Format dates for input type="date"
            const formatDate = (date) => {
              if (!date) return "";
              const parsedDate = new Date(date);
              return isNaN(parsedDate) ? "" : parsedDate.toISOString().split('T')[0];
            };
            setFormData(prev => ({
              ...prev,
              ...invoiceData,
              ausstellungsdatum: formatDate(invoiceData.ausstellungsdatum),
              zahlungsziel: formatDate(invoiceData.zahlungsziel),
            }));
          } else {
            setError(response.message || t("failed_to_fetch_data"));
            Swal.fire({
              icon: "error",
              title: t("error"),
              text: response.message || t("failed_to_fetch_data"),
              timer: 2000,
              showConfirmButton: false,
            });
          }
        } catch (err) {
          setError(t("failed_to_fetch_data"));
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("failed_to_fetch_data"),
            timer: 2000,
            showConfirmButton: false,
          });
          console.error("Error fetching invoice:", err);
        } finally {
          setLoading(false);
        }
      };

      fetchInvoice();
    } else if (location.state?.invoiceData) {
      // Handle data passed via navigate state (optional)
      const invoiceData = location.state.invoiceData;
      const formatDate = (date) => {
        if (!date) return "";
        const parsedDate = new Date(date);
        return isNaN(parsedDate) ? "" : parsedDate.toISOString().split('T')[0];
      };
      setFormData(prev => ({
        ...prev,
        ...invoiceData,
        ausstellungsdatum: formatDate(invoiceData.ausstellungsdatum),
        zahlungsziel: formatDate(invoiceData.zahlungsziel),
      }));
    }
  }, [id, location.state, t]);

  // Auto-calculate totals when price or discount changes
  useEffect(() => {
    const totals = calculateInvoiceTotals(formData.preis, formData.rabatt_prozent);
    setFormData(prev => ({ ...prev, ...totals }));
  }, [formData.preis, formData.rabatt_prozent, calculateInvoiceTotals]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === "file") {
      const file = files[0];
      setLogoFile(file);
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData(prev => ({ ...prev, logo: reader.result }));
        };
        reader.readAsDataURL(file);
      } else {
        setFormData(prev => ({ ...prev, logo: "" }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Reset file input
  const resetFileInput = () => {
    setLogoFile(null);
    setFormData(prev => ({ ...prev, logo: "" }));
    if (fileRef.current) fileRef.current.value = "";
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // علام جميع الحقول كـ touched
    const allTouched = {};
    requiredFields.forEach(field => {
      allTouched[field] = true;
    });
    setTouchedFields(allTouched);
    
    // التحقق من صحة النموذج
    const errors = validateForm();
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      
      // رسالة خطأ مبسطة
      const errorMessage = t('please_fill_all_required_fields');
      setError(errorMessage);
      
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: errorMessage,
        timer: 2000,
        showConfirmButton: false,
      });
      
      // تمرير إلى أول حقل به خطأ
      const firstErrorField = Object.keys(errors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      
      return;
    }

    try {
      setSaving(true);
      setError("");

      const formDataToSend = new FormData();
      
      // Append all form data, ensuring no undefined/null values
      Object.keys(formData).forEach((key) => {
        if (key === "logo" && logoFile) {
          formDataToSend.append("logo", logoFile);
        } else {
          formDataToSend.append(key, formData[key] ?? "");
        }
      });

      let response;
      if (id) {
        response = await adminInvoicesAPI.update(id, formDataToSend);
      } else {
        response = await adminInvoicesAPI.store(formDataToSend);
      }

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: id ? t("invoice_updated_successfully") : t("invoice_created_successfully"),
          timer: 2000,
          showConfirmButton: false,
        }).then(() => {
          navigate("/admin/invoices");
        });
      } else {
        setError(response.message || t("operation_failed"));
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t("operation_failed"),
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      setError(t("operation_failed"));
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("operation_failed"),
        timer: 2000,
        showConfirmButton: false,
      });
      console.error("Error submitting form:", err);
    } finally {
      setSaving(false);
    }
  };

  // دالة مساعدة لتحديد كلاس الحقل
  const getFieldClassName = (fieldName) => {
    const isInvalid = touchedFields[fieldName] && validationErrors[fieldName];
    return `form-control ${isInvalid ? 'is-invalid' : ''}`;
  };

  // دالة لعرض رسالة الخطأ أسفل الحقل
  const renderFieldError = (fieldName) => {
    if (touchedFields[fieldName] && validationErrors[fieldName]) {
      return (
        <Form.Control.Feedback type="invalid">
          {validationErrors[fieldName]}
        </Form.Control.Feedback>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="text-center mt-4">
        <Spinner animation="border" />
        <p className="mt-2">{t("loading")}</p>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <Card className="shadow border-0 rounded-4 p-4 custom-invoice-card bg-dashboard">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="text-sidebar fw-bold mb-0 h3">
              <span role="img" aria-label="invoice">📧</span>{" "}
              {id ? t("Edit Invoice") : t("Add Invoice")}
            </h1>
            <Button 
              variant="secondary" 
              onClick={() => navigate("/admin/invoices")}
              disabled={saving}
              aria-label={t("back_to_list")}
            >
              {t("Back to List")}
            </Button>
          </div>

          {error && (
            <Alert variant="danger" role="alert" className="mb-4">
              <Alert.Heading>{t("Error")}</Alert.Heading>
              <p>{error}</p>
            </Alert>
          )}

          <Form onSubmit={handleSubmit} noValidate>
            <section aria-labelledby="basic-info-heading">
              <h2 id="basic-info-heading" className="h5 fw-bold text-sidebar mb-3">
                {t("basic_information")}
              </h2>

              {(formData.logo || logoFile) && (
                <Form.Group className="mb-3">
                  <Form.Label>{t("Current Logo")}</Form.Label>
                  <div>
                    <Image
                      src={formData.logo}
                      alt={t("invoice_logo")}
                      width={100}
                      height={100}
                      rounded
                      style={{ objectFit: "cover" }}
                      loading="lazy"
                    />
                    <br/>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={resetFileInput}
                      disabled={saving}
                      className="mt-2"
                      aria-label={t("remove_logo")}
                    >
                      {t("remove_logo")}
                    </Button>
                  </div>
                </Form.Group>
              )}
              
              <Row className="mb-4">
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      {t("Title")} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getFieldClassName('title')}
                      required
                    />
                    {renderFieldError('title')}
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("Logo")}</Form.Label>
                    <Form.Control
                      type="file"
                      name="logo"
                      ref={fileRef}
                      onChange={handleInputChange}
                      accept="image/*"
                      aria-describedby="logoHelp"
                    />
                    <Form.Text id="logoHelp" muted>
                      {t("Upload company logo")}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </section>

            <hr />

            <section aria-labelledby="company-info-heading">
              <h2 id="company-info-heading" className="h5 fw-bold text-sidebar mb-3">
                {t("company_information")}
              </h2>
              
              {/* User Selection Dropdown */}
              <Row className="mb-4">
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("select_user")}</Form.Label>
                    <Form.Select
                      name="user_id"
                      onChange={handleUserSelect}
                      value={selectedUser?.id || ""}
                    >
                      <option value="">{t("select_user")}</option>
                      {data.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.firstname && user.lastname 
                            ? `${user.firstname} ${user.lastname} (${user.email})`
                            : user.nicname 
                            ? `${user.nicname} (${user.email})`
                            : user.email
                          }
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text muted>
                      {t("select_user_to_form")}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      {t("firma_name")}
                    </Form.Label>
                    <Form.Control
                      name="firma_name"
                      value={formData.firma_name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getFieldClassName('firma_name')}
                    />
                    {renderFieldError('firma_name')}
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      {t("kunde_name")}
                    </Form.Label>
                    <Form.Control
                      name="kunde_name"
                      value={formData.kunde_name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getFieldClassName('kunde_name')}
                    />
                    {renderFieldError('kunde_name')}
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      {t("strasse_hausnummer")}
                    </Form.Label>
                    <Form.Control
                      name="strasse_hausnummer"
                      value={formData.strasse_hausnummer}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getFieldClassName('strasse_hausnummer')}
                    />
                    {renderFieldError('strasse_hausnummer')}
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      {t("postleitzahl_staat_ort")}
                    </Form.Label>
                    <Form.Control
                      name="postleitzahl_staat_ort"
                      value={formData.postleitzahl_staat_ort}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getFieldClassName('postleitzahl_staat_ort')}
                    />
                    {renderFieldError('postleitzahl_staat_ort')}
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("land")}</Form.Label>
                    <Form.Control
                      name="land"
                      value={formData.land}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      {t("Email")}
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getFieldClassName('email')}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("ust_idnr")}</Form.Label>
                    <Form.Control
                      name="ust_idnr"
                      value={formData.ust_idnr}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </section>

            <hr />

            <section aria-labelledby="invoice-details-heading">
              <h2 id="invoice-details-heading" className="h5 fw-bold text-sidebar mb-3">
                {t("invoice_details1")}
              </h2>
              
              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("Invoice")}</Form.Label>
                    <Form.Control
                      name="rechnung"
                      value={formData.rechnung}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("unsrer_firma")}</Form.Label>
                    <Form.Control
                      name="unsrer_firma"
                      value={formData.unsrer_firma}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("postleitzahl_staat_ort_2")}</Form.Label>
                    <Form.Control
                      name="postleitzahl_staat_ort_2"
                      value={formData.postleitzahl_staat_ort_2}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("strasse_hausnummer_2")}</Form.Label>
                    <Form.Control
                      name="strasse_hausnummer_2"
                      value={formData.strasse_hausnummer_2}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("land_2")}</Form.Label>
                    <Form.Control
                      name="land_2"
                      value={formData.land_2}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("st_nr")}</Form.Label>
                    <Form.Control
                      name="st_nr"
                      value={formData.st_nr}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("ust_idnr_2")}</Form.Label>
                    <Form.Control
                      name="ust_idnr_2"
                      value={formData.ust_idnr_2}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("zaher_rawas")}</Form.Label>
                    <Form.Control
                      name="zaher_rawas"
                      value={formData.zaher_rawas}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("telefonnummer")}</Form.Label>
                    <Form.Control
                      name="telefonnummer"
                      value={formData.telefonnummer}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("email_2")}</Form.Label>
                    <Form.Control
                      type="email"
                      name="email_2"
                      value={formData.email_2}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("webseite")}</Form.Label>
                    <Form.Control
                      name="webseite"
                      value={formData.webseite}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </section>

            <hr />

            <section aria-labelledby="invoice-details-heading">
              <h2 id="invoice-details-heading" className="h5 fw-bold text-sidebar mb-3">
                {t("invoice_date")}
              </h2>
              
              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      {t("rechnungsnummer")} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="rechnungsnummer"
                      value={formData.rechnungsnummer}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getFieldClassName('rechnungsnummer')}
                    />
                    {renderFieldError('rechnungsnummer')}
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("kundennummer")}</Form.Label>
                    <Form.Control
                      name="kundennummer"
                      value={formData.kundennummer}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      {t("ausstellungsdatum")} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="ausstellungsdatum"
                      value={formData.ausstellungsdatum}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getFieldClassName('ausstellungsdatum')}
                    />
                    {renderFieldError('ausstellungsdatum')}
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      {t("zahlungsziel")} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="zahlungsziel"
                      value={formData.zahlungsziel}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getFieldClassName('zahlungsziel')}
                    />
                    {renderFieldError('zahlungsziel')}
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("zahlungsform")}</Form.Label>
                    <Form.Control
                      name="zahlungsform"
                      value={formData.zahlungsform}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </section>

            <hr />

            <Row className="mb-4">
              <Col xs={12} md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">{t("bedanken_uns")}</Form.Label>
                  <Form.Control
                    name="bedanken_uns"
                    value={formData.bedanken_uns}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <hr />

            <section aria-labelledby="items-heading">
              <h2 id="items-heading" className="h5 fw-bold text-sidebar mb-3">
                {t("Items")}
              </h2>
              
              <Row className="mb-4 g-3">
                <Col xs={12} md={2}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("menge")}</Form.Label>
                    <Form.Control
                      type="text"
                      name="menge"
                      value={formData.menge}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={2}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("menge_weight")}</Form.Label>
                    <Form.Control
                      type="number"
                      name="menge_weight"
                      value={formData.menge_weight}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={2}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("price")}</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      name="preis"
                      value={formData.preis}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={2}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("rabatt_prozent")}</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      name="rabatt_prozent"
                      value={formData.rabatt_prozent}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={2}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("betrag")}</Form.Label>
                    <Form.Control
                      name="betrag"
                      value={formData.betrag}
                      readOnly
                      aria-readonly="true"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={2}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("auftrag_nr")}</Form.Label>
                    <Form.Control
                      name="auftrag_nr"
                      value={formData.auftrag_nr}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("abholung")}</Form.Label>
                    <Form.Control
                      name="abholung"
                      value={formData.abholung}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("zustellung")}</Form.Label>
                    <Form.Control
                      name="zustellung"
                      value={formData.zustellung}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </section>

            <hr />

            <section aria-labelledby="totals-heading">
              <h2 id="totals-heading" className="h5 fw-bold text-sidebar mb-3">
                {t("totals")}
              </h2>
              
              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("betrag_2")}</Form.Label>
                    <Form.Control
                      name="betrag_2"
                      value={formData.betrag_2}
                      readOnly
                      aria-readonly="true"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("nettobetrag")}</Form.Label>
                    <Form.Control
                      name="nettobetrag"
                      value={formData.nettobetrag}
                      readOnly
                      aria-readonly="true"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("ust_19_von")}</Form.Label>
                    <Form.Control
                      name="ust_19_von"
                      value={formData.ust_19_von}
                      readOnly
                      aria-readonly="true"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("gesamtbetrag")}</Form.Label>
                    <Form.Control
                      name="gesamtbetrag"
                      value={formData.gesamtbetrag}
                      readOnly
                      aria-readonly="true"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("zu_bezahlen")}</Form.Label>
                    <Form.Control
                      name="zu_bezahlen"
                      value={formData.zu_bezahlen}
                      readOnly
                      aria-readonly="true"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </section>

            <hr />

            <section aria-labelledby="bank-details-heading">
              <h2 id="bank-details-heading" className="h5 fw-bold text-sidebar mb-3">
                {t("bank_details")}
              </h2>
              
              <Row className="mb-3">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("bankverbindung")}</Form.Label>
                    <Form.Control
                      name="bankverbindung"
                      value={formData.bankverbindung}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("zahlungsempfanger")}</Form.Label>
                    <Form.Control
                      name="zahlungsempfanger"
                      value={formData.zahlungsempfanger}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("bankname")}</Form.Label>
                    <Form.Control
                      name="bankname"
                      value={formData.bankname}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-5">
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("iban")}</Form.Label>
                    <Form.Control
                      name="iban"
                      value={formData.iban}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">{t("swift_bic")}</Form.Label>
                    <Form.Control
                      name="swift_bic"
                      value={formData.swift_bic}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </section>

            <div className="text-end" role="group" aria-label={t("Form actions")}>
              <Button
                variant="secondary"
                className="me-2"
                onClick={() => navigate("/admin/invoices")}
                disabled={saving}
                type="button"
              >
                {t("Cancel")}
              </Button>
              <Button
                variant="primary"
                className="px-4 rounded-3 shadow"
                type="submit"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {t("Saving...")}
                  </>
                ) : (
                  t("save")
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
}